import React, { useEffect, useRef, useState } from "react";
import i18n from "i18next";
import TableComponent from "./table-component";
import InlineTableEditor from "./InlineTableEditor";
import { normalizeTable, resolveOptions } from "./tableData";
require("./_inlineTableEditor.scss");
/* eslint-disable react/prop-types */

// Small stable hash so the react-datatable-bs instance remounts when its data
// or sort changes (it only reads initialData/initialSortBy on mount — undo,
// CSV import or a new sort column would otherwise not show).
function hashKey(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return h;
}

/**
 * DataTable render template for Editor and Visor.
 *
 * Visor / unselected: the read-only react-datatable-bs view (search, paging,
 * theme from resolveOptions). Editor: double-click swaps in InlineTableEditor;
 * Esc, "Done" or a pointerdown outside leaves edit mode — same interaction
 * model as AtomicElementRenderer.
 *
 * A freshly inserted table carries `__autoEdit: true` (getInitialState) and
 * opens straight in edit mode once, then clears the flag.
 *
 * @param {object}   props.state    plugin state
 * @param {function} props.update   EditorBox's (key|keys[], value|values[]) => void; absent in Visor
 * @param {boolean}  props.visor
 */
export default function TableRenderer({ state, update, visor }) {
  const canEdit = !visor && typeof update === "function";
  const [editing, setEditing] = useState(false);
  const [autoFocus, setAutoFocus] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (canEdit && state.__autoEdit) {
      update("__autoEdit", false);
      setAutoFocus(true);
      setEditing(true);
    }
  }, []);

  useEffect(() => {
    if (!editing) return undefined;
    function onDocPointerDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setEditing(false);
    }
    document.addEventListener("pointerdown", onDocPointerDown, true);
    return () => document.removeEventListener("pointerdown", onDocPointerDown, true);
  }, [editing]);

  const table = normalizeTable(state.data, state.keys);

  if (editing && canEdit) {
    return (
      <div ref={rootRef} className="dt-renderer dt-renderer--editing">
        <InlineTableEditor
          table={table}
          autoFocus={autoFocus}
          onCommit={(next) => update(["data", "keys"], [next.rows, next.keys])}
          onExit={() => setEditing(false)}
        />
      </div>
    );
  }

  const options = resolveOptions(state);
  const key = hashKey(
    JSON.stringify([table, options.sortColumn, options.initialOrder, options.initialPageLength])
  );
  return (
    <div
      ref={rootRef}
      className="dt-renderer"
      onDoubleClick={
        canEdit
          ? (e) => {
              // Paging / rows-per-page / filter clicks are browsing, not "edit".
              if (e.target.closest && e.target.closest(".pagination, .form-control")) return;
              e.stopPropagation();
              setAutoFocus(true);
              setEditing(true);
            }
          : undefined
      }
    >
      <TableComponent key={key} data={table.rows} keys={table.keys} options={options} />
      {canEdit && (
        <div className="dt-renderer__hint">
          {i18n.t("messages.double_click_to_edit", { defaultValue: "double-click to edit" })}
        </div>
      )}
    </div>
  );
}
/* eslint-enable react/prop-types */
